import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import FinalizarVendaModal from "../components/FinalizarVendaModal";
import ReciboModal from "../components/ReciboModal";
import { toast } from "react-toastify";
import NovoPedidoModal from "../components/NovoPedidoModal";
import { FaPlus, FaSearch, FaShoppingCart, FaTrash, FaUndo } from "react-icons/fa";

const formatCurrency = (valor) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(valor || 0));

export default function Vendas() {
  const [produtos, setProdutos] = useState([]);
  const [busca, setBusca] = useState("");
  const [carrinho, setCarrinho] = useState([]);
  const [mostrarFinalizarModal, setMostrarFinalizarModal] = useState(false);
  const [mostrarPedidoModal, setMostrarPedidoModal] = useState(false);
  const [recibo, setRecibo] = useState(null);
  const [mostrarItemManual, setMostrarItemManual] = useState(false);
  const [itemManual, setItemManual] = useState({ nome: "", tamanho: "", custo: "", preco: "" });

  const [carregando, setCarregando] = useState(false);
  const [erroCarregamento, setErroCarregamento] = useState(false);

  async function carregarProdutos() {
    try {
      setCarregando(true);
      setErroCarregamento(false);
      const res = await api.get("/produtos");
      setProdutos(res.data);
    } catch (err) {
      console.error("Erro ao buscar produtos:", err);
      setErroCarregamento(true);
      toast.error("Erro ao buscar produtos. Verifique a conexão ou tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarProdutos();
  }, []);

  const produtosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return [];

    return produtos
      .filter(
        (produto) =>
          produto.nome.toLowerCase().includes(termo) ||
          (produto.codigo && produto.codigo.toLowerCase().includes(termo)) ||
          (produto.variacoes || []).some((variacao) => variacao.codigoBarras === termo)
      )
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }, [produtos, busca]);

  const total = carrinho.reduce((soma, item) => soma + item.qtd * item.preco, 0);
  const totalItens = carrinho.reduce((soma, item) => soma + item.qtd, 0);

  function adicionarAoCarrinho(produto) {
    setCarrinho((prev) => {
      const existente = prev.find(
        (item) => item.variacaoId === produto.variacaoId && item.produtoId === produto.produtoId
      );

      if (existente) {
        return prev.map((item) =>
          item.variacaoId === produto.variacaoId && item.produtoId === produto.produtoId
            ? { ...item, qtd: item.qtd + 1 }
            : item
        );
      }

      return [...prev, { ...produto, qtd: produto.qtd || 1 }];
    });
    setBusca("");
  }

  function adicionarPorCodigoBarras(event) {
    if (event.key !== "Enter") return;
    const codigo = busca.trim();
    if (!codigo) return;

    for (const produto of produtos) {
      const variacao = (produto.variacoes || []).find((item) => item.codigoBarras === codigo);
      if (!variacao) continue;
      event.preventDefault();

      if (variacao.estoque <= 0) {
        toast.info(`${produto.nome} tam. ${variacao.numeracao} está sem estoque.`);
        return;
      }

      adicionarAoCarrinho({
        produtoId: produto.id,
        variacaoId: variacao.id,
        nome: produto.nome,
        preco: produto.preco,
        numeracao: variacao.numeracao,
        qtd: 1,
        estoque: variacao.estoque,
      });
      toast.success(`${produto.nome} tam. ${variacao.numeracao} adicionado.`);
      return;
    }
  }

  function removerUltimoAdicionado() {
    setCarrinho((prev) => prev.slice(0, -1));
  }

  function removerItemCarrinho(indexParaRemover) {
    setCarrinho((prev) => prev.filter((_, index) => index !== indexParaRemover));
  }

  function limparCarrinho() {
    setCarrinho([]);
    setMostrarFinalizarModal(false);
    setBusca("");
  }

  function adicionarItemManual() {
    const nome = itemManual.nome.trim();
    const tamanho = itemManual.tamanho.trim();
    const custo = Number(itemManual.custo || 0);
    const preco = Number(itemManual.preco || 0);

    if (!nome) {
      toast.error("Informe o item fora de estoque.");
      return;
    }

    if (!Number.isFinite(custo) || custo < 0 || !Number.isFinite(preco) || preco <= 0) {
      toast.error("Informe custo e valor de venda corretamente.");
      return;
    }

    const idManual = `manual-${Date.now()}`;
    setCarrinho((prev) => [
      ...prev,
      {
        produtoId: idManual,
        variacaoId: idManual,
        manual: true,
        nome,
        preco,
        custoUnitario: custo,
        outrosCustos: 0,
        numeracao: tamanho,
        qtd: 1,
      },
    ]);
    setItemManual({ nome: "", tamanho: "", custo: "", preco: "" });
    setMostrarItemManual(false);
    toast.success("Item fora de estoque adicionado.");
  }

  return (
    <div className="lojia-page min-h-screen p-4 sm:p-6">
      <div className="lojia-hero-panel mb-6 flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Nova venda</h1>
          <p className="mt-1 text-sm text-white/68">
            Busque produtos, selecione a numeração e finalize pelo carrinho.
          </p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.08] px-4 py-3 text-sm">
          <span className="font-medium text-white/62">Total atual</span>
          <p className="text-2xl font-semibold text-white">{formatCurrency(total)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <section className="lojia-surface overflow-hidden">
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-base font-semibold text-slate-950">Buscar produto</h2>
            <div className="relative mt-4">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={adicionarPorCodigoBarras}
                autoComplete="off"
                placeholder="Digite nome, código ou leia a etiqueta"
                className="w-full rounded-lg border border-[#E5DED2] bg-[#FFFEFA] py-3 pl-10 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-[#16A34A] focus:bg-white"
              />
            </div>

            <div className="mt-3 rounded-lg border border-[#E5DED2] bg-[#FFFEFA]/80 p-3">
              <button
                type="button"
                onClick={() => setMostrarItemManual((valor) => !valor)}
                className="flex w-full items-center justify-between gap-3 text-left text-sm font-semibold text-slate-900"
              >
                <span>Produto fora de estoque</span>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#16A34A]/10 text-[#0B1115]">
                  <FaPlus size={12} />
                </span>
              </button>

              {mostrarItemManual && (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_96px_120px_120px_auto] sm:items-end">
                  <label>
                    <span className="mb-1 block text-xs font-medium text-slate-500">Item</span>
                    <input
                      type="text"
                      value={itemManual.nome}
                      onChange={(e) => setItemManual((prev) => ({ ...prev, nome: e.target.value }))}
                      placeholder="Ex: Tênis Adidas"
                      className="w-full rounded-lg border border-[#E5DED2] bg-white px-3 py-2.5 text-base outline-none transition placeholder:text-slate-400 focus:border-[#16A34A] sm:text-sm"
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-slate-500">Tamanho</span>
                    <input
                      type="text"
                      value={itemManual.tamanho}
                      onChange={(e) => setItemManual((prev) => ({ ...prev, tamanho: e.target.value }))}
                      placeholder="Ex: 36"
                      className="w-full rounded-lg border border-[#E5DED2] bg-white px-3 py-2.5 text-base outline-none transition placeholder:text-slate-400 focus:border-[#16A34A] sm:text-sm"
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-slate-500">Custo</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={itemManual.custo}
                      onChange={(e) => setItemManual((prev) => ({ ...prev, custo: e.target.value }))}
                      placeholder="0,00"
                      className="w-full rounded-lg border border-[#E5DED2] bg-white px-3 py-2.5 text-base outline-none transition placeholder:text-slate-400 focus:border-[#16A34A] sm:text-sm"
                    />
                  </label>
                  <label>
                    <span className="mb-1 block text-xs font-medium text-slate-500">Venda</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={itemManual.preco}
                      onChange={(e) => setItemManual((prev) => ({ ...prev, preco: e.target.value }))}
                      placeholder="0,00"
                      className="w-full rounded-lg border border-[#E5DED2] bg-white px-3 py-2.5 text-base outline-none transition placeholder:text-slate-400 focus:border-[#16A34A] sm:text-sm"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={adicionarItemManual}
                    className="lojia-primary-action h-10 px-4 text-sm font-medium"
                  >
                    Adicionar
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="p-4">
            {busca.trim() === "" ? (
              <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-dashed border-[#E5DED2] bg-[#FFFEFA]/70 p-8 text-center">
                <div>
                  <FaSearch className="mx-auto text-2xl text-[#16A34A]" />
                  <p className="mt-3 text-sm font-medium text-slate-900">Comece buscando um produto</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Os resultados aparecerão aqui com as numerações disponíveis.
                  </p>
                </div>
              </div>
            ) : carregando ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600">
                Carregando produtos...
              </div>
            ) : erroCarregamento ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                Não foi possível carregar os produtos.
                <button onClick={carregarProdutos} className="ml-2 font-semibold underline">
                  Tentar novamente
                </button>
              </div>
            ) : produtosFiltrados.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {produtosFiltrados.map((produto) => (
                  <div key={produto.id} className="rounded-lg border border-[#E5DED2] bg-white/80 p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#16A34A]/35 hover:shadow-[0_16px_34px_rgba(36,48,43,0.1)]">
                    <div className="flex items-start gap-3">
                      <img
                        src={produto.imagemUrl || "https://cdn-icons-png.flaticon.com/512/771/771543.png"}
                        alt={produto.nome}
                        className="h-14 w-14 shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-950">{produto.nome}</p>
                        <p className="mt-1 text-xs text-slate-500">Código: {produto.codigo || "N/A"}</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900">{formatCurrency(produto.preco)}</p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                        Numeração disponível
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {produto.variacoes
                          .slice()
                          .sort((a, b) => Number(a.numeracao) - Number(b.numeracao))
                          .map((variacao) => (
                            <button
                              key={variacao.id}
                              disabled={variacao.estoque === 0}
                              onClick={() =>
                                adicionarAoCarrinho({
                                  produtoId: produto.id,
                                  variacaoId: variacao.id,
                                  nome: produto.nome,
                                  preco: produto.preco,
                                  numeracao: variacao.numeracao,
                                  qtd: 1,
                                  estoque: variacao.estoque,
                                })
                              }
                              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                variacao.estoque === 0
                                  ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 line-through"
                                  : "border-[#E5DED2] bg-white text-slate-800 hover:border-[#16A34A] hover:bg-[#16A34A]/5 hover:text-[#0B1115]"
                              }`}
                            >
                              {variacao.numeracao}
                              <span className="ml-1 text-xs text-slate-500">({variacao.estoque})</span>
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Produto não encontrado</p>
                <p className="mt-1 text-sm text-slate-500">
                  Verifique a busca ou cadastre o produto no estoque antes de concluir a venda.
                </p>
              </div>
            )}
          </div>
        </section>

        <aside className="lojia-surface overflow-hidden xl:sticky xl:top-6 xl:self-start">
          <div className="border-b border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-950">
                  <FaShoppingCart className="text-[#16A34A]" /> Carrinho
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {totalItens} itens em {carrinho.length} lançamentos
                </p>
              </div>
              {carrinho.length > 0 && (
                <button
                  onClick={removerUltimoAdicionado}
                  className="lojia-ghost-action inline-flex items-center gap-2 px-3 py-2 text-xs font-medium"
                >
                  <FaUndo size={11} /> Desfazer
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[480px] overflow-auto p-4">
            {carrinho.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#E5DED2] bg-[#FFFEFA]/70 p-6 text-center">
                <p className="text-sm font-medium text-slate-900">Carrinho vazio</p>
                <p className="mt-1 text-sm text-slate-500">Selecione uma numeração para adicionar produtos.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {carrinho.map((item, index) => (
                  <div
                    key={`${item.produtoId}-${item.variacaoId}-${index}`}
                    className="rounded-lg border border-[#E5DED2] bg-white/80 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-950">{item.nome}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {item.numeracao ? `Tam. ${item.numeracao} x ${item.qtd}` : `Qtd. ${item.qtd}`}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-start gap-2">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(item.preco * item.qtd)}
                        </p>
                        <button
                          type="button"
                          onClick={() => removerItemCarrinho(index)}
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                          aria-label={`Remover ${item.nome} do carrinho`}
                          title="Remover item"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-500">Total</span>
              <span className="text-2xl font-semibold text-slate-950">{formatCurrency(total)}</span>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <button
                className="rounded-lg bg-[#168B4B] px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(22,139,75,0.18)] transition hover:bg-[#11743E] disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setMostrarFinalizarModal(true)}
                disabled={carrinho.length === 0}
              >
                Finalizar venda
              </button>

              <button
                className="rounded-lg bg-[#0B1115] px-4 py-3 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(11,17,21,0.14)] transition hover:bg-[#172228] disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setMostrarPedidoModal(true)}
                disabled={carrinho.length === 0}
              >
                Criar pedido
              </button>
            </div>
          </div>
        </aside>
      </div>

      {mostrarPedidoModal && (
        <NovoPedidoModal
          carrinho={carrinho}
          aoFechar={() => setMostrarPedidoModal(false)}
          aoConfirmar={(pedido) => {
            if (pedido) setRecibo({ tipo: "pedido", registro: pedido });
            setMostrarPedidoModal(false);
            limparCarrinho();
            carregarProdutos();
          }}
        />
      )}

      {mostrarFinalizarModal && (
        <FinalizarVendaModal
          carrinho={carrinho}
          aoFechar={() => setMostrarFinalizarModal(false)}
          aoFinalizar={(venda) => {
            if (venda) setRecibo({ tipo: "venda", registro: venda });
            limparCarrinho();
            carregarProdutos();
          }}
        />
      )}

      <ReciboModal
        aberto={!!recibo}
        tipo={recibo?.tipo}
        registro={recibo?.registro}
        aoFechar={() => setRecibo(null)}
      />
    </div>
  );
}
